import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Copy, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";

export default function Programs() {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [sourceId, setSourceId] = useState(null);
  const [cloneGroups, setCloneGroups] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", price: 0, duration_months: 1, classes_included: 0, self_booking: false, department_id: "" });
  const [deps, setDeps] = useState([]);

  const load = () => {
    api.get("/departments").then((r) => setDeps(r.data || [])).catch(() => {});
    return api.get("/programs").then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, []);

  // Alphabetical directory: sort programs A→Z (Spanish locale, accent-insensitive).
  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })),
    [items],
  );

  const openCreate = () => { setEditing(null); setDuplicating(false); setSourceId(null); setCloneGroups(false); setForm({ name: "", description: "", price: 0, duration_months: 1, classes_included: 0, self_booking: false, department_id: deps[0]?.id || "" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setDuplicating(false); setSourceId(null); setCloneGroups(false); setForm({ name: p.name, description: p.description, price: p.price, duration_months: p.duration_months, classes_included: p.classes_included || 0, self_booking: !!p.self_booking, department_id: p.department_id || (deps[0]?.id || "") }); setOpen(true); };
  const openDuplicate = (p) => {
    setEditing(null);
    setDuplicating(true);
    setSourceId(p.id);
    setCloneGroups(false);
    setForm({
      name: `${p.name} ${t("copy_suffix")}`,
      description: p.description || "",
      price: p.price,
      duration_months: p.duration_months,
      classes_included: p.classes_included || 0,
      self_booking: !!p.self_booking,
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      const payload = { ...form, price: Number(form.price), duration_months: Number(form.duration_months), classes_included: Number(form.classes_included), self_booking: !!form.self_booking };
      if (editing) {
        await api.put(`/programs/${editing.id}`, payload);
        toast.success(t("saved"));
      } else if (duplicating && sourceId) {
        const r = await api.post(`/programs/${sourceId}/duplicate`, { ...payload, clone_groups: cloneGroups });
        const cloned = r?.data?.groups_cloned || 0;
        toast.success(cloneGroups ? `${t("saved")} · ${cloned} ${t("groups_cloned")}` : t("saved"));
      } else {
        await api.post("/programs", payload);
        toast.success(t("saved"));
      }
      setOpen(false);
      load();
    } catch { toast.error(t("error_generic")); }
  };

  const remove = async (id) => {
    if (!confirm(t("confirm_delete"))) return;
    await api.delete(`/programs/${id}`);
    toast.success(t("deleted"));
    load();
  };

  return (
    <div>
      <PageHeader
        title={t("programs")}
        testId="programs-header"
        action={
          <Button data-testid="new-program-button" onClick={openCreate}>
            <Plus className="h-4 w-4" /> {t("new_program")}
          </Button>
        }
      />
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>{t("name")}</TableHead>
              <TableHead>{t("description")}</TableHead>
              <TableHead>{t("price")}</TableHead>
              <TableHead>{t("duration_months")}</TableHead>
              <TableHead>{t("classes_included")}</TableHead>
              <TableHead className="text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">{t("no_data")}</TableCell></TableRow>
            )}
            {sortedItems.map((p) => (
              <TableRow key={p.id} data-testid={`program-row-${p.id}`}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="text-slate-600 max-w-md truncate">{p.description}</TableCell>
                <TableCell>${Number(p.price).toFixed(2)}</TableCell>
                <TableCell>{p.duration_months}</TableCell>
                <TableCell>{p.classes_included || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-program-${p.id}`} title={t("edit")}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openDuplicate(p)} data-testid={`duplicate-program-${p.id}`} title={t("duplicate")}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(p.id, p.name)} className="text-red-600" data-testid={`delete-program-${p.id}`} title={t("delete") || "Eliminar"}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? t("edit") : duplicating ? t("duplicate_program") : t("new_program")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("name")}</Label>
              <Input data-testid="program-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Departamento</Label>
              <select value={form.department_id} data-testid="program-department-select"
                      onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md border border-slate-200 px-2 text-sm">
                {deps.map((d) => <option key={d.id} value={d.id}>{d.short_name || d.name}</option>)}
              </select>
            </div>
            <div>
              <Label>{t("description")}</Label>
              <Textarea data-testid="program-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>{t("price")}</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">$</span>
                  <Input type="number" data-testid="program-price-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="pl-7 pr-12" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none">USD</span>
                </div>
              </div>
              <div>
                <Label>{t("duration_months")}</Label>
                <Input type="number" data-testid="program-duration-input" value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })} />
              </div>
              <div>
                <Label>{t("classes_included")}</Label>
                <Input type="number" data-testid="program-classes-input" value={form.classes_included} onChange={(e) => setForm({ ...form, classes_included: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-start justify-between gap-3 p-3 rounded-md bg-blue-50 border border-blue-200">
            <div className="flex-1">
              <Label htmlFor="program-self-booking" className="text-sm">{t("program_self_booking")}</Label>
              <p className="text-[11px] text-slate-600 mt-0.5">{t("program_self_booking_hint")}</p>
            </div>
            <Switch
              id="program-self-booking"
              data-testid="program-self-booking-switch"
              checked={!!form.self_booking}
              onCheckedChange={(v) => setForm({ ...form, self_booking: v })}
            />
          </div>
          {duplicating && (
            <div className="flex items-center gap-2 mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
              <Checkbox
                id="clone-groups-checkbox"
                data-testid="clone-groups-checkbox"
                checked={cloneGroups}
                onCheckedChange={(v) => setCloneGroups(Boolean(v))}
              />
              <Label htmlFor="clone-groups-checkbox" className="cursor-pointer text-sm">
                {t("clone_groups_too")}
              </Label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-program-button" onClick={submit}>{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
